import { jsx } from 'react/jsx-runtime';
import { memo, useMemo } from 'react';
import { ChatMessage } from './ChatMessage.js';

const MessageListV2 = memo(({ conversationId, messages, showTypingIndicator, streamingAIResponse, productMetadataMap, surveyMetadataMap, streamingProductGroups, currentStatus, previewSearchQueries, previewQueryProducts, isWaitingForFollowUpQueries, isActivelyStreaming, streamingFeaturedProduct, streamingStoreLocator, followUpQuestions = [], bottomRef, onSearch, onUpdateFeedback, onVariantSelect, onSuggestionClick, apiBaseUrl, }) => {
    // Combine all messages for display
    const displayMessages = useMemo(() => [
        ...messages,
        ...(showTypingIndicator
            ? [
                {
                    role: 'assistant',
                    content: '',
                    timestamp: new Date(),
                    isTyping: true,
                },
            ]
            : []),
        // Add a streaming message if:
        // 1. We have streaming AI response content, OR
        // 2. We're actively streaming and have preview data (even if no AI response yet)
        ...(streamingAIResponse ||
            (isActivelyStreaming && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0))
            ? [
                {
                    role: 'assistant',
                    content: streamingAIResponse,
                    timestamp: new Date(),
                    isStreaming: true,
                },
            ]
            : []),
    ], [
        messages,
        showTypingIndicator,
        streamingAIResponse,
        isActivelyStreaming,
        previewSearchQueries.length,
        previewQueryProducts.length,
    ]);
    return (jsx("div", Object.assign({ style: {
            width: '100%',
            maxWidth: '768px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
        } }, { children: displayMessages.map((message, displayIndex) => {
            var _a;
            const hasStreamingMessage = !!streamingAIResponse ||
                (isActivelyStreaming && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0));
            const isLastMessage = displayIndex === messages.length + (showTypingIndicator ? 1 : 0) + (hasStreamingMessage ? 1 : 0) - 1;
            const isTyping = 'isTyping' in message && message.isTyping;
            const isStreaming = 'isStreaming' in message && message.isStreaming;
            // Show streaming featured product on the streaming assistant message
            const showStreamingFeaturedProduct = isStreaming && streamingFeaturedProduct;
            // Show streaming store locator on the streaming assistant message
            const showStreamingStoreLocator = isStreaming && streamingStoreLocator;
            // For streaming messages, use conversation-level preview data
            // For regular messages, use message-specific preview data if available
            !isStreaming &&
                !isTyping &&
                'previewSearchQueries' in message &&
                message.previewSearchQueries &&
                message.previewSearchQueries.length > 0;
            // Generate a stable key that doesn't change when streaming becomes a regular message
            // For typing indicator, use a special key
            // For streaming messages, use the next message index (since they'll become a regular message)
            // For regular messages, use their index in the messages array
            const messageKey = isTyping
                ? 'typing-indicator'
                : isStreaming
                    ? `message-${messages.length}` // Use the index where this message will be added
                    : `message-${displayIndex}`;
            // Get follow-up queries from the message itself (per-message storage)
            const messageFollowUpQueries = 'followUpQueries' in message ? message.followUpQueries : [];
            // Show suggestions on assistant messages that have follow-up queries and aren't streaming/typing
            const showFollowUpQueries = !isTyping &&
                !isStreaming &&
                message.role === 'assistant' &&
                messageFollowUpQueries &&
                messageFollowUpQueries.length > 0;
            return (jsx(ChatMessage, { bottomRef: isLastMessage ? bottomRef : undefined, role: message.role, content: message.content, isTyping: isTyping, isStreaming: isStreaming, isLastMessage: isLastMessage, conversationId: conversationId || undefined, messageIndex: 'messageIndex' in message ? message.messageIndex : !isTyping && !isStreaming ? displayIndex : undefined, feedback: 'feedback' in message ? message.feedback : undefined, followUpQueries: showFollowUpQueries ? messageFollowUpQueries : [], nextUserMessage: 
                // Find the next user message after this assistant message to derive which follow-up was clicked
                displayIndex < messages.length - 1 && ((_a = messages[displayIndex + 1]) === null || _a === void 0 ? void 0 : _a.role) === 'user'
                    ? messages[displayIndex + 1].content
                    : undefined, productMetadataMap: productMetadataMap, surveyMetadataMap: surveyMetadataMap, streamingProductGroups: streamingProductGroups, currentStatus: isLastMessage ? currentStatus : undefined, previewSearchQueries: isStreaming && isLastMessage
                    ? previewSearchQueries
                    : 'previewSearchQueries' in message
                        ? message.previewSearchQueries || []
                        : [], previewQueryProducts: isStreaming && isLastMessage
                    ? previewQueryProducts
                    : 'previewQueryProducts' in message
                        ? message.previewQueryProducts || []
                        : [], isWaitingForFollowUpQueries: isLastMessage ? isWaitingForFollowUpQueries : false, featuredProduct: 'featuredProduct' in message ? message.featuredProduct : undefined, streamingFeaturedProduct: showStreamingFeaturedProduct ? streamingFeaturedProduct : undefined, storeLocator: 'storeLocator' in message ? message.storeLocator : undefined, streamingStoreLocator: showStreamingStoreLocator ? streamingStoreLocator : undefined, imageUrl: 'imageUrl' in message ? message.imageUrl : undefined, imageData: 'imageData' in message ? message.imageData : undefined, imageMimeType: 'imageMimeType' in message ? message.imageMimeType : undefined, orders: 'orders' in message ? message.orders : undefined, onSearch: onSearch, onUpdateFeedback: onUpdateFeedback, onVariantSelect: onVariantSelect, onSuggestionClick: onSuggestionClick, apiBaseUrl: apiBaseUrl }, messageKey));
        }) })));
});

export { MessageListV2 };
//# sourceMappingURL=MessageListV2.js.map
